import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	AlertTriangle,
	ArrowLeft,
	Building2,
	Calendar,
	CheckCircle2,
	Circle,
	ClipboardCheck,
	Clock,
	DollarSign,
	Download,
	Eye,
	FileText,
	Info,
	Loader2,
	Trophy,
	Upload,
	XCircle,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useTRPC } from "@/integrations/trpc/react";
import { useAuth } from "@/lib/auth";
import { VendorLayout } from "@/components/layouts/VendorLayout";
import { useBuyerContext } from "@/contexts/vendor-buyer-context";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";

export const Route = createFileRoute("/vendor/$buyerSlug/bids/$bidId/")({
	component: VendorBidDetailPage,
});


function getBidStatusColor(status: string) {
	switch (status) {
		case "draft":
			return "bg-yellow-100 text-yellow-700";
		case "submitted":
			return "bg-blue-100 text-blue-700";
		case "under_review":
			return "bg-purple-100 text-purple-700";
		case "awarded":
			return "bg-green-100 text-green-700";
		case "rejected":
			return "bg-red-100 text-red-700";
		default:
			return "bg-gray-100 text-gray-700";
	}
}

function getStatusIcon(status: string) {
	switch (status) {
		case "awarded":
			return <Trophy className="h-5 w-5 text-green-600" />;
		case "rejected":
			return <XCircle className="h-5 w-5 text-red-600" />;
		default:
			return <Clock className="h-5 w-5 text-blue-600" />;
	}
}

function VendorBidDetailPage() {
	const buyer = useBuyerContext();
	const { buyerSlug, bidId } = Route.useParams();
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const { user } = useAuth();
	const vendorEmail = user?.email ?? "";
	const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
	const [downloadingFormId, setDownloadingFormId] = useState<string | null>(null);
	const [viewingFormId, setViewingFormId] = useState<string | null>(null);
	const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);

	// Query the bid
	const { data: bidData, isLoading: bidLoading } = useQuery(
		trpc.vendorPortal.bids.get.queryOptions({
			bidId,
		}),
	);

	const solicitationId = bidData?.solicitation_id;

	// Query solicitation data (only if we have the solicitation ID)
	const { data: solicitationData, isLoading: solicitationLoading } = useQuery({
		...trpc.public.solicitations.get.queryOptions({
			solicitationId: solicitationId ?? "",
		}),
		enabled: !!solicitationId,
	});

	// Query forms for this solicitation
	const { data: formsData } = useQuery({
		...trpc.public.solicitationDetails.forms.queryOptions({
			solicitationId: solicitationId ?? "",
		}),
		enabled: !!solicitationId,
	});

	// Query required documents for this solicitation
	const { data: documentsData } = useQuery({
		...trpc.public.solicitationDetails.documents.queryOptions({
			solicitationId: solicitationId ?? "",
		}),
		enabled: !!solicitationId,
	});

	// Query existing form submissions for this vendor
	const { data: formSubmissionsData } = useQuery({
		...trpc.vendorPortal.formResponses.listByVendor.queryOptions({
			vendorEmail,
		}),
		enabled: !!vendorEmail,
	});

	// Query existing document uploads for this vendor
	const { data: documentSubmissionsData } = useQuery({
		...trpc.vendorPortal.documentUploads.listByVendor.queryOptions({
			vendorEmail,
		}),
		enabled: !!vendorEmail,
	});

	// Query compliance checks for this solicitation
	const { data: complianceChecksData } = useQuery({
		...trpc.public.solicitationDetails.complianceChecks.queryOptions({
			solicitationId: solicitationId ?? "",
		}),
		enabled: !!solicitationId,
	});

	// Query compliance acknowledgments for this bid
	const { data: complianceAcksData } = useQuery({
		...trpc.vendorPortal.bids.getComplianceAcknowledgments.queryOptions({
			bidId,
		}),
		enabled: !!bidId,
	});

	// Withdraw bid mutation with optimistic update
	const withdrawBidMutation = useMutation(
		trpc.vendorPortal.bids.withdraw.mutationOptions({
			onMutate: async () => {
				// Cancel any outgoing refetches to avoid overwriting optimistic update
				await queryClient.cancelQueries({
					queryKey: trpc.vendorPortal.bids.get.queryKey({ bidId }),
				});

				// Snapshot the previous value
				const previousBid = queryClient.getQueryData(
					trpc.vendorPortal.bids.get.queryKey({ bidId }),
				);

				// Optimistically update to the new value
				queryClient.setQueryData(
					trpc.vendorPortal.bids.get.queryKey({ bidId }),
					(old: typeof bidData) => old ? { ...old, status: "withdrawn" } : old,
				);

				// Close the dialog immediately for better UX
				setWithdrawDialogOpen(false);

				// Return context with the previous value
				return { previousBid };
			},
			onSuccess: () => {
				toast.success("Bid withdrawn successfully");
				queryClient.invalidateQueries({ queryKey: ["vendorPortal", "bids"] });
			},
			onError: (error, _variables, context) => {
				// Rollback to the previous value on error
				if (context?.previousBid) {
					queryClient.setQueryData(
						trpc.vendorPortal.bids.get.queryKey({ bidId }),
						context.previousBid,
					);
				}
				toast.error(`Failed to withdraw bid: ${error.message}`);
				// Reopen the dialog on error so user can try again
				setWithdrawDialogOpen(true);
			},
			onSettled: () => {
				// Always refetch after error or success
				queryClient.invalidateQueries({
					queryKey: trpc.vendorPortal.bids.get.queryKey({ bidId }),
				});
			},
		}),
	);

	// Mutation for generating filled PDF
	const generateFilledPdfMutation = useMutation(
		trpc.pdfTemplates.generateFilledPdfFromSubmission.mutationOptions(),
	);

	// Mutation for getting document download URL
	const getDownloadUrlMutation = useMutation(
		trpc.vendorPortal.documentUploads.getDownloadUrl.mutationOptions(),
	);

	const formatCurrency = (amount: number) => {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
		}).format(amount);
	};

	const formatFileSize = (bytes: number | null) => {
		if (!bytes) return "0 KB";
		if (bytes < 1024) return `${bytes} B`;
		if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
		return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	};

	// Loading state
	const isLoading = bidLoading || solicitationLoading;

	if (isLoading) {
		return (
			<VendorLayout buyerName={buyer.name} buyerSlug={buyerSlug}>
				<div className="flex items-center justify-center py-12">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			</VendorLayout>
		);
	}

	if (!bidData) {
		return (
			<VendorLayout buyerName={buyer.name} buyerSlug={buyerSlug}>
				<div className="text-center py-12">
					<h2 className="text-2xl font-bold">Bid not found</h2>
					<p className="text-muted-foreground mt-2">
						The bid you're looking for doesn't exist.
					</p>
					<Button asChild className="mt-4">
						<Link to="/vendor/$buyerSlug/bids" params={{ buyerSlug }}>Back to My Bids</Link>
					</Button>
				</div>
			</VendorLayout>
		);
	}

	// Prepare display data
	const bid = {
		id: bidData.id,
		solicitationId: bidData.solicitation_id,
		solicitationTitle: solicitationData?.title ?? "Unknown Solicitation",
		organization: "Organization", // Would need org lookup
		status: bidData.status,
		submittedAt: bidData.submitted_at ?? bidData.created_at,
		notes: bidData.notes,
	};

	// Map form submissions to display format
	// Note: formsData returns { forms, archivedForms }, so access .forms
	const allForms = formsData?.forms ?? [];
	const submittedForms = (formSubmissionsData ?? [])
		.filter((fs) => {
			// Match forms to this solicitation's forms
			if (!allForms.length) return false;
			return allForms.some((f) => f.id === fs.solicitation_form_id);
		})
		.map((fs) => {
			const formDef = allForms.find((f) => f.id === fs.solicitation_form_id);
			return {
				id: fs.id,
				name: formDef?.title ?? "Form",
				completedAt: fs.submitted_at ?? fs.created_at,
			};
		});

	// Handle viewing filled form PDFs (opens in new tab)
	const handleViewFilledForm = async (submissionId: string) => {
		setViewingFormId(submissionId);
		try {
			const result = await generateFilledPdfMutation.mutateAsync({
				submissionId,
			});
			if (result?.pdfBase64) {
				const byteCharacters = atob(result.pdfBase64);
				const byteNumbers = new Array(byteCharacters.length);
				for (let i = 0; i < byteCharacters.length; i++) {
					byteNumbers[i] = byteCharacters.charCodeAt(i);
				}
				const byteArray = new Uint8Array(byteNumbers);
				const blob = new Blob([byteArray], { type: "application/pdf" });
				const url = URL.createObjectURL(blob);
				window.open(url, "_blank");
			}
		} catch (error) {
			console.error("View error:", error);
			toast.error("Failed to view form PDF");
		} finally {
			setViewingFormId(null);
		}
	};

	// Handle downloading filled form PDFs
	const handleDownloadFilledForm = async (submissionId: string, title: string) => {
		setDownloadingFormId(submissionId);
		try {
			const result = await generateFilledPdfMutation.mutateAsync({
				submissionId,
			});
			if (result?.pdfBase64) {
				// Convert base64 to blob and trigger download
				const byteCharacters = atob(result.pdfBase64);
				const byteNumbers = new Array(byteCharacters.length);
				for (let i = 0; i < byteCharacters.length; i++) {
					byteNumbers[i] = byteCharacters.charCodeAt(i);
				}
				const byteArray = new Uint8Array(byteNumbers);
				const blob = new Blob([byteArray], { type: "application/pdf" });
				const url = URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = url;
				link.download = result.filename || `${title} - Filled.pdf`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);
			}
		} catch (error) {
			console.error("Download error:", error);
			toast.error("Failed to download filled form PDF");
		} finally {
			setDownloadingFormId(null);
		}
	};

	// Map document submissions to display format
	const submittedDocuments = (documentSubmissionsData ?? [])
		.filter((ds) => {
			// Match docs to this solicitation's requested documents
			if (!documentsData) return false;
			return documentsData.some((d) => d.id === ds.requested_document_id);
		})
		.map((ds) => {
			const docDef = documentsData?.find((d) => d.id === ds.requested_document_id);
			return {
				id: ds.id,
				name: ds.original_filename ?? docDef?.title ?? "Document",
				uploadedAt: ds.uploaded_at ?? ds.created_at,
				size: formatFileSize(ds.file_size),
				filePath: ds.file_path,
			};
		});

	// Handle downloading uploaded documents
	const handleDownloadDocument = async (doc: { id: string; name: string; filePath: string | null }) => {
		if (!doc.filePath) {
			toast.error("Document file path not available");
			return;
		}
		setDownloadingDocId(doc.id);
		try {
			const result = await getDownloadUrlMutation.mutateAsync({
				filePath: doc.filePath,
			});
			if (result?.signedUrl) {
				// Create a temporary link and trigger download
				const link = document.createElement("a");
				link.href = result.signedUrl;
				link.download = doc.name;
				link.target = "_blank";
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			}
		} catch (error) {
			console.error("Download error:", error);
			toast.error("Failed to download document");
		} finally {
			setDownloadingDocId(null);
		}
	};

	const canWithdraw =
		bid.status === "submitted" || bid.status === "draft";
	const canModify = bid.status === "draft";

	const handleWithdraw = () => {
		if (!vendorEmail) {
			toast.error("Please log in to withdraw bid");
			return;
		}
		withdrawBidMutation.mutate({
			bidId,
			vendorEmail,
		});
	};

	return (
		<VendorLayout buyerName={buyer.name} buyerSlug={buyerSlug}>
			<div className="space-y-6">
				{/* Header */}
				<div className="flex items-start justify-between">
					<div className="space-y-1">
						<div className="flex items-center gap-2">
							<Button variant="ghost" size="icon" asChild>
								<Link to="/vendor/$buyerSlug/bids" params={{ buyerSlug }}>
									<ArrowLeft className="h-4 w-4" />
								</Link>
							</Button>
							<Badge className={getBidStatusColor(bid.status)}>
								{bid.status}
							</Badge>
						</div>
						<h1 className="text-2xl font-bold tracking-tight">
							{bid.solicitationTitle}
						</h1>
						<div className="flex items-center gap-4 text-sm text-muted-foreground">
							<span className="flex items-center gap-1">
								<Building2 className="h-4 w-4" />
								{bid.organization}
							</span>
							<span className="flex items-center gap-1">
								<Calendar className="h-4 w-4" />
								Submitted {new Date(bid.submittedAt).toLocaleDateString()}
							</span>
						</div>
					</div>
					{(canWithdraw || canModify) && (
						<div className="flex gap-2">
							{canModify && (
								<Button variant="outline" asChild>
									<Link to="/vendor/$buyerSlug/bids/$bidId/edit" params={{ buyerSlug, bidId }}>
										Modify Bid
									</Link>
								</Button>
							)}
							{canWithdraw && (
								<Button
									variant="destructive"
									onClick={() => setWithdrawDialogOpen(true)}
								>
									Withdraw Bid
								</Button>
							)}
						</div>
					)}
				</div>

				{/* Status Banner */}
				{bid.status === "submitted" && (
					<div className="space-y-3">
						<div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
							<Clock className="h-5 w-5 text-blue-600 mt-0.5" />
							<div>
								<h3 className="font-medium text-blue-800">
									Your bid has been submitted.
								</h3>
								<p className="text-sm text-blue-700 mt-1">
									Your submission is being reviewed by the buyer.
								</p>
							</div>
						</div>
						<div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
							<Info className="h-5 w-5 text-amber-600 mt-0.5" />
							<div>
								<h3 className="font-medium text-amber-800">
									Submitted bids cannot be modified.
								</h3>
								<p className="text-sm text-amber-700 mt-1">
									If you need to make changes, please withdraw your bid and submit a new one before the deadline.
								</p>
							</div>
						</div>
					</div>
				)}

				{bid.status === "draft" && (
					<div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
						<AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
						<div>
							<h3 className="font-medium text-yellow-800">
								This bid is still a draft.
							</h3>
							<p className="text-sm text-yellow-700 mt-1">
								Complete and submit your bid before the deadline.
							</p>
						</div>
					</div>
				)}

				{bid.status === "withdrawn" && (
					<div className="p-4 bg-gray-50 border border-gray-200 rounded-lg flex items-start gap-3">
						<XCircle className="h-5 w-5 text-gray-600 mt-0.5" />
						<div>
							<h3 className="font-medium text-gray-800">
								This bid has been withdrawn.
							</h3>
						</div>
					</div>
				)}

				<div className="grid gap-6 lg:grid-cols-3">
					{/* Main Content */}
					<div className="lg:col-span-2 space-y-6">
						{/* Bid Summary */}
						<Card>
							<CardHeader>
								<CardTitle>Bid Summary</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="grid gap-4 md:grid-cols-2">
									<div className="p-4 border rounded-lg">
										<div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
											<Calendar className="h-4 w-4" />
											{bid.status === "submitted" ? "Submission Date" : "Created Date"}
										</div>
										<p className="text-xl font-bold">
											{bid.submittedAt
												? new Date(bid.submittedAt).toLocaleDateString()
												: "Not submitted"}
										</p>
									</div>
									<div className="p-4 border rounded-lg">
										<div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
											<Calendar className="h-4 w-4" />
											Due Date
										</div>
										<p className="text-xl font-bold">
											{solicitationData?.due_date
												? new Date(solicitationData.due_date).toLocaleDateString()
												: "N/A"}
										</p>
									</div>
								</div>
								{bid.notes && (
									<div className="mt-4 p-4 bg-muted/50 rounded-lg">
										<p className="text-sm text-muted-foreground mb-1">Notes</p>
										<p className="text-sm">{bid.notes}</p>
									</div>
								)}
							</CardContent>
						</Card>

						{/* Submitted Forms */}
						<Card>
							<CardHeader>
								<CardTitle className="flex items-center gap-2">
									<FileText className="h-5 w-5" />
									{bid.status === "submitted" ? "Submitted Forms" : "Required Forms"}
								</CardTitle>
								<CardDescription>
									{bid.status === "submitted"
										? "Forms completed as part of your submission"
										: "Forms required for this solicitation"}
								</CardDescription>
							</CardHeader>
							<CardContent>
								{submittedForms.length > 0 ? (
									<div className="space-y-3">
										{submittedForms.map((form) => (
											<div
												key={form.id}
												className="flex items-center justify-between p-3 border rounded-lg"
											>
												<div className="flex items-center gap-3">
													<CheckCircle2 className="h-5 w-5 text-blue-600" />
													<div>
														<span className="font-medium">{form.name}</span>
														<p className="text-sm text-muted-foreground">
															Completed{" "}
															{new Date(form.completedAt).toLocaleDateString()}
														</p>
													</div>
												</div>
												<div className="flex items-center gap-1">
													<Tooltip>
														<TooltipTrigger asChild>
															<Button
																variant="ghost"
																size="sm"
																onClick={() => handleViewFilledForm(form.id)}
																disabled={viewingFormId === form.id}
															>
																{viewingFormId === form.id ? (
																	<Loader2 className="h-4 w-4 animate-spin" />
																) : (
																	<Eye className="h-4 w-4" />
																)}
															</Button>
														</TooltipTrigger>
														<TooltipContent>View form</TooltipContent>
													</Tooltip>
													<Tooltip>
														<TooltipTrigger asChild>
															<Button
																variant="ghost"
																size="sm"
																onClick={() => handleDownloadFilledForm(form.id, form.name)}
																disabled={downloadingFormId === form.id}
															>
																{downloadingFormId === form.id ? (
																	<Loader2 className="h-4 w-4 animate-spin" />
																) : (
																	<Download className="h-4 w-4" />
																)}
															</Button>
														</TooltipTrigger>
														<TooltipContent>Download form</TooltipContent>
													</Tooltip>
												</div>
											</div>
										))}
									</div>
								) : (
									<div className="text-center py-6 text-muted-foreground">
										<FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
										<p>No forms have been submitted yet</p>
									</div>
								)}
							</CardContent>
						</Card>

						{/* Uploaded Documents */}
						<Card>
							<CardHeader>
								<CardTitle className="flex items-center gap-2">
									<Upload className="h-5 w-5" />
									{bid.status === "submitted" ? "Uploaded Documents" : "Required Documents"}
								</CardTitle>
								<CardDescription>
									{bid.status === "submitted"
										? "Documents submitted with your bid"
										: "Documents required for this solicitation"}
								</CardDescription>
							</CardHeader>
							<CardContent>
								{submittedDocuments.length > 0 ? (
									<div className="space-y-3">
										{submittedDocuments.map((doc) => (
											<div
												key={doc.id}
												className="flex items-center justify-between p-3 border rounded-lg"
											>
												<div className="flex items-center gap-3">
													<FileText className="h-5 w-5 text-muted-foreground" />
													<div>
														<span className="font-medium">{doc.name}</span>
														<p className="text-sm text-muted-foreground">
															{doc.size} • Uploaded{" "}
															{new Date(doc.uploadedAt).toLocaleDateString()}
														</p>
													</div>
												</div>
												<Tooltip>
												<TooltipTrigger asChild>
													<Button
														variant="ghost"
														size="sm"
														onClick={() => handleDownloadDocument(doc)}
														disabled={downloadingDocId === doc.id}
													>
														{downloadingDocId === doc.id ? (
															<Loader2 className="h-4 w-4 animate-spin" />
														) : (
															<Download className="h-4 w-4" />
														)}
													</Button>
												</TooltipTrigger>
												<TooltipContent>Download: {doc.name}</TooltipContent>
											</Tooltip>
											</div>
										))}
									</div>
								) : (
									<div className="text-center py-6 text-muted-foreground">
										<Upload className="h-8 w-8 mx-auto mb-2 opacity-50" />
										<p>No documents have been uploaded yet</p>
									</div>
								)}
							</CardContent>
						</Card>
					</div>

					{/* Sidebar */}
					<div className="space-y-6">
						{/* Status */}
						<Card>
							<CardHeader>
								<CardTitle>Status</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="flex items-center gap-3">
									{getStatusIcon(bid.status)}
									<div>
										<p className="font-medium capitalize">
											{bid.status.replace("_", " ")}
										</p>
										<p className="text-sm text-muted-foreground">
											{bid.status === "awarded" && bid.awardedAt
												? `Awarded ${new Date(bid.awardedAt).toLocaleDateString()}`
												: `Submitted ${new Date(bid.submittedAt).toLocaleDateString()}`}
										</p>
									</div>
								</div>
							</CardContent>
						</Card>

						{/* Compliance */}
						{complianceChecksData && complianceChecksData.length > 0 && (
							<Card>
								<CardHeader>
									<CardTitle className="flex items-center gap-2">
										<ClipboardCheck className="h-5 w-5" />
										Compliance
									</CardTitle>
								</CardHeader>
								<CardContent>
									<div className="space-y-2">
										{complianceChecksData.map((check) => {
											const isAcknowledged = complianceAcksData?.some(
												(ack) => ack.solicitation_compliance_check_id === check.id && ack.acknowledged
											);
											return (
												<div
													key={check.id}
													className="flex items-center gap-2 text-sm"
												>
													{isAcknowledged ? (
														<CheckCircle2 className="h-4 w-4 text-blue-600" />
													) : (
														<Circle className="h-4 w-4 text-muted-foreground" />
													)}
													<span>{check.title}</span>
												</div>
											);
										})}
									</div>
								</CardContent>
							</Card>
						)}

						{/* Solicitation Info */}
						{solicitationData && (
							<Card>
								<CardHeader>
									<CardTitle>Solicitation Details</CardTitle>
								</CardHeader>
								<CardContent className="space-y-3">
									<div>
										<p className="text-sm text-muted-foreground">Type</p>
										<p className="font-medium">{solicitationData.type}</p>
									</div>
									<Separator />
									<div>
										<p className="text-sm text-muted-foreground">Due Date</p>
										<p className="font-medium">
											{solicitationData.due_date
											? new Date(solicitationData.due_date).toLocaleDateString()
											: "No deadline"}
										</p>
									</div>
									<div>
										<p className="text-sm text-muted-foreground">Status</p>
										<p className="font-medium capitalize">{solicitationData.status}</p>
									</div>
									<Separator />
									<Button variant="outline" className="w-full" asChild>
										<Link
											to="/vendor/$buyerSlug/solicitations/$solicitationId"
											params={{ buyerSlug, solicitationId: bid.solicitationId }}
										>
											View Solicitation
										</Link>
									</Button>
								</CardContent>
							</Card>
						)}
					</div>
				</div>
			</div>

			{/* Withdraw Dialog */}
			<Dialog open={withdrawDialogOpen} onOpenChange={setWithdrawDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<AlertTriangle className="h-5 w-5 text-red-500" />
							Withdraw Bid
						</DialogTitle>
						<DialogDescription>
							Are you sure you want to withdraw your bid for "
							{bid.solicitationTitle}"? This action cannot be undone.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setWithdrawDialogOpen(false)}
						>
							Cancel
						</Button>
						<Button variant="destructive" onClick={handleWithdraw}>
							Withdraw Bid
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</VendorLayout>
	);
}
